/**
 * GameInfo — left rail player list. Renders two PlayerCards (white, then
 * black) plus a small turn/pool readout. The active player's card glows
 * via the PlayerCard `isActive` prop.
 *
 * `myColor` is forwarded so each card knows whether to show "(나)".
 * Turn changes are announced by the single `aria-live` region in TurnBanner
 * (the active-game spec mandates exactly one live region), so this rail is
 * intentionally NOT a live region — a second would double-announce.
 */

import type { PlayerColor, YinshState } from "@cloudnest/yinsh-engine";
import { PlayerCard } from "./PlayerCard";

interface GameInfoProps {
  readonly gameState: YinshState;
  readonly myColor: PlayerColor | null;
  /** Display name for the WHITE player (host) sourced from the lobby room. */
  readonly whiteName: string;
  /** Display name for the BLACK player (guest) sourced from the lobby room. */
  readonly blackName: string;
}

export function GameInfo({
  gameState,
  myColor,
  whiteName,
  blackName,
}: GameInfoProps) {
  const [white, black] = gameState.players;

  return (
    <div
      data-testid="player-rail"
      style={{ display: "flex", flexDirection: "column", gap: 12 }}
    >
      <PlayerCard
        name={whiteName}
        color="WHITE"
        ringsScored={white.ringsScored}
        isActive={gameState.currentPlayer === "WHITE"}
        isYou={myColor === "WHITE"}
      />
      <PlayerCard
        name={blackName}
        color="BLACK"
        ringsScored={black.ringsScored}
        isActive={gameState.currentPlayer === "BLACK"}
        isYou={myColor === "BLACK"}
      />
      <div
        data-testid="turn-meta"
        style={{
          fontSize: 12,
          color: "var(--cn-text-muted)",
          padding: "0 4px",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <span>턴 {gameState.turn}</span>
        <span>마커 {gameState.markersPool}</span>
      </div>
    </div>
  );
}
